import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import '../../styles/components/profile_page/EditProfileModal.css';

const EditProfileModal = ({
  onClose,
  initialSection,
  profileImageUrl,
  coverPhotoUrl,
  bio,
  setBio,
  userAttributes,
  setProfileImageUrl,
  setCoverPhotoUrl,
  setSpecialities,
  travelData,
  packages: initialPackages,
  setTravelData,
  setPackages
}) => {
  const { authFetch } = useAuth();
  const photographerSub = userAttributes?.sub;
  const isPhotographer = userAttributes?.['custom:userRole'] === 'photographer';
  const specialitiesHeadingRef = useRef(null);
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [specialitiesLoadError, setSpecialitiesLoadError] = useState(false);
  const [specialitiesAttempt, setSpecialitiesAttempt] = useState(0);

  useEffect(() => {
    if (initialSection === 'specialities') {
      specialitiesHeadingRef.current?.focus();
      specialitiesHeadingRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [initialSection]);

  // Profile photo state
  const profileInputRef = useRef(null);
  const [profilePreview, setProfilePreview] = useState(profileImageUrl);
  const [profileFile, setProfileFile] = useState(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [profileUploadFailed, setProfileUploadFailed] = useState(false);

  // Cover photo state
  const coverInputRef = useRef(null);
  const [coverPreview, setCoverPreview] = useState(coverPhotoUrl);
  const [coverFile, setCoverFile] = useState(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);

  // Bio
  const [isUpdatingProfileContent, setIsUpdatingProfileContent] = useState(false);

  // Travel (NEW)
  const [travelBaseLocation, setTravelBaseLocation] = useState('');
  const [travelFreeRadius, setTravelFreeRadius] = useState(0);
  const [travelExtraPerMile, setTravelExtraPerMile] = useState(0);
  const [travelRegions, setTravelRegions] = useState('');
  const [travelNotes, setTravelNotes] = useState('');
  

  // Packages (NEW - JSON textarea MVP)
  const [packages, setLocalPackages] = useState([]);

  // Specialities
  const predefinedSpecialities = [
    'Weddings','Portrait','Events','Commercial','Fashion','Product','Real Estate','Food',
    'Travel','Lifestyle','Sports','Newborn & Maternity','Pets','Architectural','Fine Art',
    'Aerial/Drone','Corporate','Boudoir','Automotive','Other'
  ];
  const [selectedSpecialities, setSelectedSpecialities] = useState([]);
  const [customSpecialities, setCustomSpecialities] = useState('');

  const [specialitiesLoaded, setSpecialitiesLoaded] = useState(false);

  // Keep saving disabled until the persisted selections have been loaded.
  useEffect(() => {
    const controller = new AbortController();
    const init = async () => {
      setSpecialitiesLoadError(false);
      setSpecialitiesLoaded(false);
      if (!isPhotographer) {
        setSpecialitiesLoaded(true);
        return;
      }
      if (!photographerSub) return;
      try {
        const res = await fetch(
          `https://7ivi6888a9.execute-api.eu-west-2.amazonaws.com/dev/photographers/${encodeURIComponent(photographerSub)}/specialities`,
          { signal: controller.signal }
        );
        const data = await res.json();
        if (!res.ok || !Array.isArray(data)) throw new Error('Specialities could not be loaded');
        if (!controller.signal.aborted) {
          const predefined = data.filter(s => !s.is_custom).map(s => s.speciality);
          const custom = data.filter(s => s.is_custom).map(s => s.speciality).join(', ');
          setSelectedSpecialities(predefined);
          setCustomSpecialities(custom);
          setSpecialitiesLoaded(true);
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          console.error('Failed to load specialities:', e);
          setSpecialitiesLoadError(true);
        }
      }
    };
    init();
    return () => controller.abort();
  }, [photographerSub, isPhotographer, specialitiesAttempt]);

  useEffect(() => {
    if (!travelData) return;

    setTravelBaseLocation(travelData.base_location || '');
    setTravelFreeRadius(travelData.free_radius_km || 0);
    setTravelExtraPerMile(travelData.extra_travel_per_mile || 0);
    setTravelRegions((travelData.regions || []).join(', '));
    setTravelNotes((travelData.notes || []).join('\n'));
  }, [travelData]);

  useEffect(() => {
    if (!initialPackages) return;

    setLocalPackages(
      initialPackages.map(p => ({
        ...p,
        includesText: (p.includes || []).join('\n')
      }))
    );
  }, [initialPackages]);

  useEffect(() => {
    if (customSpecialities.trim()) {
      if (!selectedSpecialities.includes('Other')) {
        setSelectedSpecialities(prev => [...prev, 'Other']);
      }
    } else {
      setSelectedSpecialities(prev => prev.filter(s => s !== 'Other'));
    }
  }, [customSpecialities]);

  const onToggleSpeciality = (speciality) => {
    if (speciality === 'Other') return;
    setSelectedSpecialities(prev =>
      prev.includes(speciality)
        ? prev.filter(s => s !== speciality)
        : [...prev, speciality]
    );
  };

  // Profile image handlers (unchanged)
  const clickChangeProfile = () => profileInputRef.current?.click();
  const onProfileSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setProfileFile(f);
    setProfilePreview(URL.createObjectURL(f));
    setProfileUploadFailed(false);
  };

  const clickChangeCover = () => coverInputRef.current?.click();
  const onCoverSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  // Upload profile (unchanged)
  const uploadProfileIfChosen = async () => {
    if (!profileFile) return;
    setIsUploadingProfile(true);
    try {
      const presignEndpoint = `https://g3xew8hcjk.execute-api.eu-west-2.amazonaws.com/dev/user-images/${userAttributes.sub}/presigned`;
      const items = [{ kind: 'profile', group: 'current', contentType: profileFile.type || 'application/octet-stream' }];

      const res = await authFetch(presignEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error();
      const [p] = await res.json();

      const put = await fetch(p.presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': p.contentType,
          'x-amz-meta-kind': 'profile',
          'x-amz-meta-group': 'current'
        },
        body: profileFile
      });
      if (!put.ok) throw new Error();

      setProfileImageUrl(profilePreview);
    } catch {
      setProfileUploadFailed(true);
      setProfilePreview(profileImageUrl);
    } finally {
      setIsUploadingProfile(false);
    }
  };

  // Upload cover (unchanged)
  const uploadCoverIfChosen = async () => {
    if (!coverFile) return;
    setIsUploadingCover(true);
    try {
      const presignEndpoint = `https://g3xew8hcjk.execute-api.eu-west-2.amazonaws.com/dev/user-images/${userAttributes.sub}/presigned`;
      const items = [{ kind: 'cover', group: 'current', contentType: coverFile.type || 'application/octet-stream' }];

      const res = await authFetch(presignEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error();
      const [p] = await res.json();

      const put = await fetch(p.presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': p.contentType,
          'x-amz-meta-kind': 'cover',
          'x-amz-meta-group': 'current'
        },
        body: coverFile
      });

      if (!put.ok) throw new Error();

      setCoverPhotoUrl(coverPreview);
    } finally {
      setIsUploadingCover(false);
    }
  };

  const addPackage = () => {
    setLocalPackages((prev) => [
      ...prev,
      { name: '', price: '', duration: '', includesText: '' }
    ]);
  };

  const removePackage = (index) => {
    setLocalPackages((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePackage = (index, field, value) => {
    setLocalPackages((prev) =>
      prev.map((pkg, i) =>
        i === index ? { ...pkg, [field]: value } : pkg
      )
    );
  };

  // Update profile_content (NEW unified PATCH)
  const updateProfileContent = async () => {
    setIsUpdatingProfileContent(true);
    try {
      const normalisedPackages = packages.map(p => ({
        name: p.name,
        price: p.price,
        duration: p.duration,
        includes: (p.includesText || '')
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)
      }));

      const payload = {
        bio,
        travel: {
          base_location: travelBaseLocation,
          free_radius_km: Number(travelFreeRadius),
          extra_travel_per_mile: Number(travelExtraPerMile),
          regions: travelRegions.split(',').map(r => r.trim()).filter(Boolean),
          notes: travelNotes.split('\n').map(n => n.trim()).filter(Boolean)
        },
        packages: normalisedPackages
      };

      const res = await authFetch(
        `https://40n03951u0.execute-api.eu-west-2.amazonaws.com/dev/users/${userAttributes.sub}/profile-content`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const t = await res.text();
        throw new Error(`profile content update failed: ${res.status} ${t}`);
      }
      setTravelData(payload.travel);
      setPackages(normalisedPackages);
    } catch (e) {
      console.error(e);
    } finally {
      setIsUpdatingProfileContent(false);
    }
  };

  const updateSpecialitiesIfChanged = async () => {
    if (userAttributes['custom:userRole'] !== 'photographer') return;

    const customList = customSpecialities.split(',').map(s => s.trim()).filter(Boolean);

    const payload = [
      ...selectedSpecialities.filter(s => s !== 'Other').map(s => ({ speciality: s, is_custom: false })),
      ...customList.map(s => ({ speciality: s, is_custom: true }))
    ];

    const response = await authFetch(
      `https://7ivi6888a9.execute-api.eu-west-2.amazonaws.com/dev/photographers/${userAttributes.sub}/specialities`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ specialities: payload })
      }
    );
    if (!response.ok) throw new Error('We could not save your specialities. Please try again.');
    setSpecialities(payload);
  };

  const handleSaveChanges = async () => {
    setSaveError('');
    setIsSaving(true);
    try {
      await Promise.all([
        uploadProfileIfChosen(),
        uploadCoverIfChosen(),
        updateProfileContent(),
        updateSpecialitiesIfChanged()
      ]);
      onClose();
    } catch (e) {
      console.error('Save failed:', e);
      setSaveError(e.message || 'We could not save your changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="edit-profile-modal-content">
        <button className="modal-close-button" onClick={onClose} aria-label="Close edit profile">×</button>
        <h2>Edit Profile</h2>

        <div className="edit-profile-section">
          <h3>Profile Picture</h3>
          <img src={profilePreview} alt="Profile" className="modal-profile-image" />
          <button className="change-photo-button" onClick={clickChangeProfile}>Change Profile Picture</button>
          <input type="file" ref={profileInputRef} accept="image/jpeg,image/png,image/webp" onChange={onProfileSelected} hidden />
          {isUploadingProfile && <p>Uploading...</p>}
          {profileUploadFailed && <p style={{ color: 'red' }}>Upload failed.</p>}
        </div>

        <div className="edit-profile-section">
          <h3>Cover Photo</h3>
          <img src={coverPreview} alt="Cover" className="modal-cover-image" />
          <button className="change-photo-button" onClick={clickChangeCover}>Change Cover Photo</button>
          <input type="file" ref={coverInputRef} accept="image/jpeg,image/png,image/webp" onChange={onCoverSelected} hidden />
          {isUploadingCover && <p>Uploading cover...</p>}
        </div>

        <div className="edit-profile-section">
          <h3>Bio</h3>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} />
        </div>

        {/* ===== Travel ===== */}
        <div className="edit-profile-section">
          <h3>Travel & Coverage</h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Base location</label>
              <input
                type="text"
                value={travelBaseLocation}
                onChange={(e) => setTravelBaseLocation(e.target.value)}
                placeholder="e.g. Manchester, UK"
              />
            </div>

            <div className="form-group">
              <label>Free travel radius (km)</label>
              <input
                type="number"
                value={travelFreeRadius}
                onChange={(e) => setTravelFreeRadius(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Extra travel cost (£ per mile)</label>
              <input
                type="number"
                step="0.01"
                value={travelExtraPerMile}
                onChange={(e) => setTravelExtraPerMile(e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Regions covered</label>
              <input
                type="text"
                value={travelRegions}
                onChange={(e) => setTravelRegions(e.target.value)}
                placeholder="UK, Europe"
              />
            </div>

            <div className="form-group full-width">
              <label>Additional notes</label>
              <textarea
                value={travelNotes}
                onChange={(e) => setTravelNotes(e.target.value)}
                placeholder="One note per line"
              />
            </div>
          </div>
        </div>

        {/* ===== Packages ===== */}
        <div className="edit-profile-section">
          <h3>Packages</h3>

          <div className="packages-list">
            {packages.map((pkg, index) => (
              <div key={index} className="package-card">
                <div className="package-header">
                  <input
                    type="text"
                    value={pkg.name}
                    onChange={(e) => updatePackage(index, 'name', e.target.value)}
                    placeholder="Package name"
                  />

                  <button
                    className="remove-package-btn"
                    onClick={() => removePackage(index)}
                  >
                    ×
                  </button>
                </div>
            
                <div className="form-grid">
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="text"
                      value={pkg.price}
                      onChange={(e) => updatePackage(index, 'price', e.target.value)}
                      placeholder="£299"
                    />
                  </div>
            
                  <div className="form-group">
                    <label>Duration</label>
                    <input
                      type="text"
                      value={pkg.duration}
                      onChange={(e) => updatePackage(index, 'duration', e.target.value)}
                      placeholder="2 hours"
                    />
                  </div>
                </div>
            
                <div className="form-group full-width">
                  <label>What's included (one per line)</label>
                  <textarea
                    value={pkg.includesText || ''}
                      onChange={(e) =>
                        updatePackage(index, 'includesText', e.target.value)
                      }
                  />
                </div>
              </div>
            ))}
          </div>
          
          <button className="add-package-btn" onClick={addPackage}>
            + Add Package
          </button>
        </div>

        {userAttributes['custom:userRole'] === 'photographer' && (
          <div className="edit-profile-section">
            <h3 ref={specialitiesHeadingRef} tabIndex={-1}>Specialities</h3>
            {!specialitiesLoaded && !specialitiesLoadError && <p role="status">Loading specialities…</p>}
            {specialitiesLoadError && (
              <div role="alert">
                <p>We could not load your specialities.</p>
                <button type="button" onClick={() => setSpecialitiesAttempt((value) => value + 1)}>Try again</button>
              </div>
            )}
            <div className="checkboxes">
            {predefinedSpecialities.map((speciality) => (
              <label key={speciality}>
                <input
                  type="checkbox"
                  checked={
                    speciality === 'Other'
                      ? customSpecialities.trim() !== ''
                      : selectedSpecialities.includes(speciality)
                  }
                  disabled={speciality === 'Other'}
                  onChange={() => onToggleSpeciality(speciality)}
                />
                {speciality}
              </label>
            ))}
            </div>
            <textarea
              placeholder="Custom specialities (comma separated)"
              value={customSpecialities}
              onChange={(e) => setCustomSpecialities(e.target.value)}
            />
          </div>
        )}

        <div className="edit-profile-modal-actions">
          <button className="save-button" onClick={handleSaveChanges}
            disabled={isSaving || (isPhotographer && (!specialitiesLoaded || specialitiesLoadError))}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
          <button className="cancel-button" onClick={onClose}>Cancel</button>
        </div>

        {saveError && <p role="alert">{saveError}</p>}

        {isUpdatingProfileContent && <p>Updating profile content...</p>}
      </div>
    </div>
  );
};

export default EditProfileModal;
